import { useCallback, useEffect, useRef, useState } from 'react';
import { io } from 'socket.io-client';
import { apiJson } from '../api/client.js';
import { CHAT_SOCKET_PATH, getChatSocketUrl } from '../utils/chatSocket.js';

/**
 * @param {object} options
 * @param {() => Promise<{ token: string }>} options.fetchSocketToken
 * @param {number|null} options.conversationId
 * @param {string|null} options.guestToken
 * @param {boolean} [options.enabled]
 */
export function useChatSocket({ fetchSocketToken, conversationId, guestToken, enabled = true }) {
  const socketRef = useRef(null);
  const conversationIdRef = useRef(conversationId);
  const guestTokenRef = useRef(guestToken);
  const [connected, setConnected] = useState(false);
  const [connecting, setConnecting] = useState(false);
  const [messages, setMessages] = useState([]);
  const [conversation, setConversation] = useState(null);
  const [error, setError] = useState('');

  conversationIdRef.current = conversationId;
  guestTokenRef.current = guestToken;

  const appendMessage = useCallback((msg) => {
    setMessages((prev) => {
      if (prev.some((m) => m.id === msg.id)) return prev;
      return [...prev, msg];
    });
  }, []);

  const emitJoin = useCallback((socket) => {
    const cid = conversationIdRef.current;
    if (!cid || !socket?.connected) return;

    socket.emit(
      'chat:join',
      { conversation_id: cid, guest_token: guestTokenRef.current || undefined },
      (res) => {
        if (!res?.ok) {
          setError(res?.error || 'Failed to join chat');
          return;
        }
        setError('');
        if (res.conversation) setConversation(res.conversation);
        if (Array.isArray(res.messages)) setMessages(res.messages);
      },
    );
  }, []);

  useEffect(() => {
    if (!enabled || !conversationId) return undefined;

    let cancelled = false;

    (async () => {
      setError('');
      setConnecting(true);
      setConnected(false);
      try {
        const { token } = await fetchSocketToken();
        if (cancelled) return;

        const socket = io(getChatSocketUrl(), {
          path: CHAT_SOCKET_PATH,
          auth: { token },
          transports: ['polling', 'websocket'],
          reconnection: true,
          reconnectionAttempts: Infinity,
        });
        socketRef.current = socket;

        socket.on('connect', () => {
          setConnected(true);
          setConnecting(false);
          setError('');
          emitJoin(socket);
        });
        socket.on('disconnect', () => setConnected(false));
        socket.on('connect_error', (err) => {
          setConnecting(false);
          setConnected(false);
          setError(
            err.message?.includes('xhr poll error') || err.message?.includes('websocket error')
              ? 'Cannot reach chat server. Ensure the chat service is running (docker compose up -d chat).'
              : err.message || 'Chat connection failed',
          );
        });
        socket.on('chat:message', (msg) => appendMessage(msg));
        socket.on('chat:conversation', (c) => setConversation(c));
      } catch (e) {
        if (!cancelled) {
          setConnecting(false);
          setError(e.message || 'Chat connection failed');
        }
      }
    })();

    return () => {
      cancelled = true;
      socketRef.current?.disconnect();
      socketRef.current = null;
      setConnected(false);
      setConnecting(false);
    };
  }, [enabled, conversationId, fetchSocketToken, appendMessage, emitJoin]);

  useEffect(() => {
    const socket = socketRef.current;
    if (!socket?.connected || !conversationId) return;
    emitJoin(socket);
  }, [conversationId, guestToken, emitJoin]);

  const sendMessage = useCallback(
    (body) =>
      new Promise((resolve, reject) => {
        const socket = socketRef.current;
        if (!socket?.connected) {
          reject(new Error('Not connected'));
          return;
        }
        socket.emit(
          'chat:message',
          {
            conversation_id: conversationId,
            body,
            guest_token: guestToken || undefined,
          },
          (res) => {
            if (res?.ok) resolve(res.message);
            else reject(new Error(res?.error || 'Send failed'));
          },
        );
      }),
    [conversationId, guestToken],
  );

  return {
    connected,
    connecting,
    messages,
    conversation,
    error,
    sendMessage,
    appendMessage,
    setMessages,
    setConversation,
  };
}

export async function fetchStaffSocketToken(conversationId) {
  const q = conversationId ? `?conversation_id=${conversationId}` : '';
  return apiJson(`/api/admin/chat/socket-token${q}`, { method: 'POST' });
}

export async function fetchClientSocketToken(conversationId) {
  const q = conversationId ? `?conversation_id=${conversationId}` : '';
  return apiJson(`/api/my/chat/socket-token${q}`, { method: 'POST' });
}
